<script setup lang="ts">
import { computed, nextTick, onMounted, ref, shallowRef, useTemplateRef, watch } from 'vue'
import { useAbility } from '@casl/vue'
import { Subject } from '@/@vxapp/@Enums/PHP/Auth/Ability/Subjects/DataProviders/AbilitySubject'
import { A } from '@/@vxapp/@Enums/PHP/Auth/DataProviders/AbilityAction'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { PaginationField } from '@/@vxapp/@Enums/PaginationField'
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'
import { isSuccess } from '@/@vxapp/@Utils/http'
import {
  RevenueStreamField,
  type RevenueStream,
  type RevenueStreamLedgerQuery,
} from '@/@vxapp/RevenueStreams/revenueStreamContract'
import { useRevenueStreamStore } from '@/@vxapp/RevenueStreams/useRevenueStreamStore'
import { cloneReactiveData } from '@/@core/utils/cloneReactiveData'
import RevenueStreamDialog from '@/components/Dialogs/RevenueStreams/RevenueStreamDialog.vue'
import { describeRevenueStreamFailure } from '@/components/Dialogs/RevenueStreams/revenueStreamFailure'
import ServiceListToolbar from '@/components/ServiceLists/Shared/ServiceListToolbar.vue'
import RevenueStreamsDatatable from '@/components/Tables/RevenueStreams/RevenueStreamsDatatable.vue'
import { DialogDisplayType } from '@/enums/dialog-display-type'
import { useAppNotifications } from '@/plugins/toast/composables/useAppNotifications'

definePage({
  meta: { ...PageMeta.RevenueStreams.list.meta, pageTitle: PageMeta.RevenueStreams.list.title },
})

const log = createLogger('RevenueStreamsListPage')
const notify = useAppNotifications()
const route = useRoute()
const router = useRouter()
const store = useRevenueStreamStore()
const { can } = useAbility()
const ledger = useTemplateRef<InstanceType<typeof RevenueStreamsDatatable>>('ledger')
const selectedStream = ref<RevenueStream | null>(null)
const isDialogVisible = shallowRef(false)
const isDeleteDialogVisible = shallowRef(false)
const isDeleting = shallowRef(false)
const displayType = ref<number>(DialogDisplayType.CREATE)
const resultCount = shallowRef(0)

const canCreate = computed(() => can(A.CREATE, Subject.REVENUESTREAMS_CREATE))
const canView = computed(() => can(A.READ, Subject.REVENUESTREAMS_PREVIEW_GUID))
const canRename = computed(() => can(A.UPDATE, Subject.REVENUESTREAMS_UPDATE_GUID))
const canDelete = computed(() => can(A.DELETE, Subject.REVENUESTREAMS_DELETE_GUID))

const defaults: RevenueStreamLedgerQuery = {
  sort: '-id',
  page: PaginationField.QueryParams.page,
  limit: PaginationField.QueryParams.limit,
  search: undefined,
}

const limitItems = [10, 25, 50, 100]

const sortItems = [
  { title: 'Newest streams', value: '-id' },
  { title: 'Oldest streams', value: '+id' },
]

function scalar(value: unknown) {
  const normalized = Array.isArray(value) ? value[0] : value

  return typeof normalized === 'string' || typeof normalized === 'number'
    ? String(normalized)
    : undefined
}
function positiveInteger(value: unknown, fallback: number) {
  const parsed = Number.parseInt(scalar(value) ?? '', 10)

  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback
}
function normalize(source: Record<string, unknown>): RevenueStreamLedgerQuery {
  const limit = positiveInteger(source.limit, defaults.limit)
  const search = scalar(source.search)?.trim()

  return {
    ...defaults,
    page: positiveInteger(source.page, defaults.page),
    limit: limitItems.includes(limit) ? limit : defaults.limit,
    sort: scalar(source.sort) === '+id' ? '+id' : '-id',
    search: search || undefined,
  }
}
function same(left: RevenueStreamLedgerQuery, right: RevenueStreamLedgerQuery) {
  return (
    left.page === right.page &&
    left.limit === right.limit &&
    left.sort === right.sort &&
    left.search === right.search
  )
}
function applyRoute() {
  const incoming = normalize(route.query as Record<string, unknown>)
  const current = normalize({ ...store.queryParams })

  if (same(current, incoming)) return false
  store.queryParams = incoming

  return true
}
function routeQuery(source: Record<string, unknown>) {
  const query = normalize(source)

  return {
    ...(query.page !== defaults.page ? { page: String(query.page) } : {}),
    ...(query.limit !== defaults.limit ? { limit: String(query.limit) } : {}),
    ...(query.sort !== defaults.sort ? { sort: query.sort } : {}),
    ...(query.search ? { search: query.search } : {}),
  }
}
async function fetchStreams() {
  await ledger.value?.fetchRevenueStreams()
}
async function updateQuery(patch: Partial<RevenueStreamLedgerQuery>) {
  const next = normalize({ ...store.queryParams, ...patch })

  store.queryParams = next

  const query = routeQuery({ ...next })

  if (JSON.stringify(route.query) !== JSON.stringify(query)) await router.replace({ query })
  await nextTick()
  await fetchStreams()
}
async function handleSearch(searchQuery: string) {
  await updateQuery({ search: searchQuery || undefined, page: 1 })
}
async function handleClear() {
  await updateQuery({ ...defaults })
}
async function handleSort(value: unknown) {
  await updateQuery({ sort: String(value) as RevenueStreamLedgerQuery['sort'], page: 1 })
}
async function handleLimit(value: unknown) {
  await updateQuery({ limit: Number(value), page: 1 })
}
async function handlePage(page: number) {
  await updateQuery({ page })
}
function handleLoaded(count: number) {
  resultCount.value = count
}

function handleCreate() {
  selectedStream.value = null
  displayType.value = DialogDisplayType.CREATE
  isDialogVisible.value = true
}
async function loadStream(guid: string) {
  const result = await store.fetch(guid, { force: true })

  if (!isSuccess(result.responseCode) || !result.data) {
    log.error('Failed to load revenue stream', { guid, responseCode: result.responseCode })
    notify.error(describeRevenueStreamFailure(result).formError)

    return false
  }
  selectedStream.value = cloneReactiveData(result.data)

  return true
}
async function handleRename(guid: string) {
  if (await loadStream(guid)) {
    displayType.value = DialogDisplayType.EDIT
    isDialogVisible.value = true
  }
}
async function handleView(guid: string) {
  if (await loadStream(guid)) {
    displayType.value = DialogDisplayType.VIEW
    isDialogVisible.value = true
  }
}
async function handleDelete(guid: string) {
  if (await loadStream(guid)) isDeleteDialogVisible.value = true
}
function handleDialogVisibility(params: { visibility: boolean }) {
  isDialogVisible.value = params.visibility
}
async function handleSaved() {
  await fetchStreams()
}
async function confirmDelete() {
  const guid = selectedStream.value?.[RevenueStreamField.DB_IDENTIFIER]

  if (!guid || isDeleting.value) return
  isDeleting.value = true
  try {
    const result = await store.delete(guid)

    if (isSuccess(result.responseCode)) {
      notify.success('Revenue stream deleted')
      isDeleteDialogVisible.value = false
      await fetchStreams()

      return
    }
    notify.error(describeRevenueStreamFailure(result).formError)
  } finally {
    isDeleting.value = false
  }
}
function closeDelete() {
  isDeleteDialogVisible.value = false
}

store.queryParams = { ...defaults }
applyRoute()
onMounted(fetchStreams)
watch(
  () => route.query,
  async () => {
    if (!applyRoute()) return
    await nextTick()
    await fetchStreams()
  },
  { deep: true }
)
</script>

<template>
  <main
    class="revenue-workspace"
    data-testid="revenue-streams-workspace"
  >
    <VCard class="revenue-workspace__hero">
      <VCardItem>
        <div class="revenue-workspace__hero-content">
          <div>
            <span class="revenue-workspace__eyebrow">Commercial routing</span>
            <VCardTitle class="revenue-workspace__title">Revenue streams</VCardTitle>
            <VCardSubtitle class="revenue-workspace__subtitle">
              Maintain the commercial streams that connect work, businesses, and finance context.
            </VCardSubtitle>
          </div>
          <div class="revenue-workspace__metric">
            <span>Current view</span>
            <strong>{{ resultCount.toLocaleString() }}</strong>
          </div>
        </div>
      </VCardItem>
      <VDivider />
      <VCardActions class="revenue-workspace__actions">
        <VBtn
          v-if="canCreate"
          color="primary"
          prepend-icon="tabler-plus"
          data-testid="revenue-streams-create"
          @click="handleCreate"
        >
          New revenue stream
        </VBtn>
        <VBtn
          prepend-icon="tabler-refresh"
          variant="tonal"
          @click="fetchStreams"
        >
          Refresh
        </VBtn>
      </VCardActions>
    </VCard>
    <ServiceListToolbar
      :query-params="store.queryParams"
      :result-count="resultCount"
      :sort-items="sortItems"
      heading="Find the next revenue stream to manage"
      limit-label="Revenue streams per page"
      plural-label="revenue streams"
      search-label="Search revenue streams"
      search-placeholder="Stream name"
      singular-label="revenue stream"
      sort-label="Sort revenue streams"
      @clear="handleClear"
      @limit-change="handleLimit"
      @refresh="fetchStreams"
      @search="handleSearch"
      @sort-change="handleSort"
    />
    <RevenueStreamsDatatable
      ref="ledger"
      :query-params="store.queryParams"
      :can-view="canView"
      :can-rename="canRename"
      :can-delete="canDelete"
      @delete="handleDelete"
      @rename="handleRename"
      @loaded="handleLoaded"
      @page-change="handlePage"
      @view="handleView"
    />
  </main>
  <RevenueStreamDialog
    :is-dialog-visible="isDialogVisible"
    :mode="displayType"
    :revenue-stream="selectedStream"
    @update:is-dialog-visible="handleDialogVisibility"
    @saved="handleSaved"
  />
  <VDialog
    v-model="isDeleteDialogVisible"
    max-width="420"
    ><VCard v-if="selectedStream">
      <VCardTitle>Delete revenue stream?</VCardTitle
      ><VCardText
        >This removes “{{ selectedStream.streamName }}” from the commercial ledger.</VCardText
      >
      <VCardActions
        ><VBtn @click="closeDelete">Cancel</VBtn
        ><VBtn
          color="error"
          :loading="isDeleting"
          data-testid="revenue-stream-confirm-delete"
          @click="confirmDelete"
          >Delete</VBtn
        ></VCardActions
      >
    </VCard></VDialog
  >
</template>

<style scoped lang="scss">
.revenue-workspace {
  display: grid;
  min-inline-size: 0;
  gap: 1.5rem;
  font-feature-settings: 'cv01', 'ss03';
}
.revenue-workspace > * {
  box-sizing: border-box;
  inline-size: 100%;
  max-inline-size: 100%;
  min-inline-size: 0;
}
.revenue-workspace__hero {
  overflow: hidden;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  background:
    radial-gradient(circle at 88% -30%, rgba(var(--v-theme-primary), 0.2), transparent 28rem),
    linear-gradient(110deg, rgba(var(--v-theme-surface-bright), 0.98), rgb(var(--v-theme-surface)));
  box-shadow: 0 18px 52px rgba(var(--v-theme-on-background), 0.06);
}

.revenue-workspace__hero-content {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;
  padding-block: 0.4rem;
}

.revenue-workspace__eyebrow {
  color: rgb(var(--v-theme-primary));
  font-size: 0.6875rem;
  font-weight: 590;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.revenue-workspace__title {
  margin-block-start: 0.35rem;
  font-size: clamp(1.75rem, 3vw, 2.35rem);
  font-weight: 590;
  letter-spacing: -0.04em;
}

.revenue-workspace__subtitle {
  max-inline-size: 48rem;
  margin-block-start: 0.65rem;
  white-space: normal;
}

.revenue-workspace__metric {
  display: grid;
  min-inline-size: 7rem;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  background: rgba(var(--v-theme-surface), 0.7);
  gap: 0.2rem;
  padding: 0.75rem 0.9rem;
}

.revenue-workspace__metric span {
  color: rgba(var(--v-theme-on-surface), 0.56);
  font-size: 0.6875rem;
  text-transform: uppercase;
}

.revenue-workspace__metric strong {
  font-size: 1.25rem;
  font-weight: 590;
}

.revenue-workspace__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
}

@media (max-width: 599px) {
  .revenue-workspace {
    gap: 1rem;
  }

  .revenue-workspace__hero-content {
    align-items: stretch;
    flex-direction: column;
    gap: 1rem;
  }

  .revenue-workspace__metric {
    min-inline-size: 0;
  }

  .revenue-workspace__actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .revenue-workspace__actions :deep(.v-btn) {
    min-block-size: 3rem;
    inline-size: 100%;
  }
}
</style>
